import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiService } from "../../services/api";
import checkPermission from "../../utils/permission";
import { useModal } from "../../hooks/useModal";
import SectionManager from "./components/SectionManager";
import ParticipantManager from "./components/ParticipantManager";
import DepartmentSelector from "../../components/selectors/DepartmentSelector";

const SURVEY_ACTION_OPTIONS = [
  { value: "NONE", label: "Hiçbir şey" },
  { value: "ASSIGN_FIXED_DEPARTMENT", label: "Departmana ata" },
  { value: "ASK_DEPARTMENT_ON_REVIEW", label: "Departman seçimi iste" },
  { value: "DELETE_USER", label: "Kullanıcının hesabını sil" },
  { value: "DEACTIVATE_USER", label: "Kullanıcının hesabını pasife al" },
];

export default function EditSurveyPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const hasEditPermission = checkPermission("survey_update");
  const { showAlert } = useModal();

  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700";
  const selectFieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50";
  const radioCardClass = (selected: boolean) =>
    `flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
      selected
        ? "border-blue-300 bg-blue-50/80 ring-4 ring-blue-50"
        : "border-slate-200/90 bg-slate-50 hover:border-slate-300 hover:bg-white"
    }`;

  useEffect(() => {
    if (!hasEditPermission) {
      navigate("/surveys");
    }
  }, [hasEditPermission, navigate]);

  const toDateTimeLocal = (value?: string | Date | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
  };

  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "SURVEY",
    isActive: true,
    startDate: "",
    endDate: "",
    languages: ["tr"],
    pagination: "PAGINATION",
    processType: "PERCENTAGE",
    isActionEnabled: false,
    approveActionType: "NONE",
    rejectActionType: "NONE",
    approveDepartmentId: "",
    rejectDepartmentId: "",
    metadata: {},
  });

  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [reviewDepartments, setReviewDepartments] = useState<Array<{ id: string; name: string; color?: string; parentId?: string | null }>>([]);

  useEffect(() => {
    const loadSurvey = async () => {
      if (!id) {
        navigate("/surveys", { replace: true });
        return;
      }

      try {
        setInitialLoading(true);
        const response = await ApiService.getSurvey(id);
        const survey = response?.data || response;

        if (!survey) {
          throw new Error("Anket bulunamadı");
        }

        setForm({
          title: survey.title || "",
          description: survey.description || "",
          type: survey.type || "SURVEY",
          isActive: survey.isActive !== false,
          startDate: toDateTimeLocal(survey.startDate),
          endDate: toDateTimeLocal(survey.endDate),
          languages: survey.languages || ["tr"],
          pagination: survey.pagination || "PAGINATION",
          processType: survey.processType || "PERCENTAGE",
          isActionEnabled: survey.isActionEnabled || false,
          approveActionType: survey.approveActionType || "NONE",
          rejectActionType: survey.rejectActionType || "NONE",
          approveDepartmentId: survey.approveDepartmentId || "",
          rejectDepartmentId: survey.rejectDepartmentId || "",
          metadata: survey.metadata || {},
        });
      } catch (error: any) {
        console.error("Anket yüklenirken hata:", error);
        await showAlert({
          title: "Anket Bulunamadı",
          message: "Aradığınız anket bulunamadı veya silinmiş olabilir.",
          type: "error",
        });
        navigate("/surveys", { replace: true });
      } finally {
        setInitialLoading(false);
      }
    };

    loadSurvey();
  }, [id, navigate, showAlert]);

  useEffect(() => {
    const loadReviewDepartments = async () => {
      try {
        const departments = await ApiService.getMySurveyReviewDepartments();
        setReviewDepartments(Array.isArray(departments) ? departments : []);
      } catch (error) {
        console.error("Review departmanları yüklenemedi:", error);
        setReviewDepartments([]);
      }
    };

    loadReviewDepartments();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !id) return;

    try {
      setIsLoading(true);
      const payload: any = {
        type: form.type,
        title: form.title,
        startDate: form.startDate,
        endDate: form.endDate,
        languages: form.languages,
        isActive: form.isActive,
        pagination: form.pagination,
        processType: form.processType,
        isActionEnabled: form.isActionEnabled,
        approveActionType: form.isActionEnabled ? form.approveActionType : "NONE",
        rejectActionType: form.isActionEnabled ? form.rejectActionType : "NONE",
        approveDepartmentId:
          form.isActionEnabled && form.approveActionType === "ASSIGN_FIXED_DEPARTMENT"
            ? form.approveDepartmentId || null
            : null,
        rejectDepartmentId:
          form.isActionEnabled && form.rejectActionType === "ASSIGN_FIXED_DEPARTMENT"
            ? form.rejectDepartmentId || null
            : null,
        metadata: form.metadata,
      };

      if (
        form.isActionEnabled &&
        form.approveActionType === "ASSIGN_FIXED_DEPARTMENT" &&
        !form.approveDepartmentId
      ) {
        await showAlert({
          title: "Uyarı",
          message: "Onay aksiyonu için bir departman seçmelisiniz.",
          type: "warning",
        });
        return;
      }

      if (
        form.isActionEnabled &&
        form.rejectActionType === "ASSIGN_FIXED_DEPARTMENT" &&
        !form.rejectDepartmentId
      ) {
        await showAlert({
          title: "Uyarı",
          message: "Ret aksiyonu için bir departman seçmelisiniz.",
          type: "warning",
        });
        return;
      }

      if (form.description && form.description.trim()) {
        payload.description = form.description.trim();
      }

      if (!form.startDate || !form.endDate) {
        await showAlert({
          title: "Uyarı",
          message: "Başlangıç ve bitiş tarihi zorunludur.",
          type: "warning",
        });
        return;
      }

      if (new Date(form.endDate).getTime() <= new Date(form.startDate).getTime()) {
        await showAlert({
          title: "Uyarı",
          message: "Bitiş tarihi başlangıç tarihinden sonra olmalıdır.",
          type: "warning",
        });
        return;
      }

      await ApiService.updateSurvey(id, payload);
      await showAlert({
        title: "Başarılı",
        message: "Anket güncellendi!",
        type: "success",
      });
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error.message || "Anket güncellenemedi.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasEditPermission) {
    return null;
  }

  return (
    <div className="h-[88vh] bg-gray-100 overflow-hidden flex flex-col">
      <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Anketler</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <button
                type="button"
                onClick={() => navigate("/surveys")}
                className="pb-3 transition-all duration-200 text-gray-900 font-semibold border-b-2 border-gray-900">
                Tüm Anketler
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Anketi Düzenle</h1>
                <p className="text-sm text-gray-600 mt-1">Anket bilgilerini güncelleyin, bölümleri ve katılımcıları yönetin.</p>
                <div className="mt-3 h-1 w-20 rounded-full bg-blue-200" />
              </div>
              <div className="text-right">
                {form.title && (
                  <div>
                    <p className="text-gray-900 font-semibold text-base sm:text-lg">{form.title}</p>
                    <p className="text-gray-600 text-sm">
                      {form.type === "REVIEW" ? "İnceleme" : "Anket"}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <h2 className="text-lg font-semibold text-gray-900 mb-4">Anket Bilgileri</h2>

            {initialLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : (
              <div className="space-y-6">
                <form id="edit-survey-form" onSubmit={handleUpdate} className="space-y-6">
                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Temel Bilgiler</span>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Anket Başlığı *</label>
                        <input
                          name="title"
                          id="survey_title"
                          autoComplete="off"
                          value={form.title}
                          onChange={handleChange}
                          className={fieldClassName}
                          placeholder="Örn: 2025 Çalışan Memnuniyet Anketi"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama</label>
                        <textarea
                          name="description"
                          id="survey_description"
                          autoComplete="off"
                          value={form.description}
                          onChange={handleChange}
                          rows={4}
                          className={fieldClassName}
                          placeholder="Örn: Bu anket, çalışanlarımızın iş ortamı, yönetim ve kariyer gelişimi hakkındaki görüşlerini almak amacıyla hazırlanmıştır."
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Başlangıç Tarihi *</label>
                          <input
                            type="datetime-local"
                            name="startDate"
                            id="survey_start_date"
                            value={form.startDate}
                            onChange={handleChange}
                            className={fieldClassName}
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Bitiş Tarihi *</label>
                          <input
                            type="datetime-local"
                            name="endDate"
                            id="survey_end_date"
                            value={form.endDate}
                            onChange={handleChange}
                            className={fieldClassName}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={sectionClassName}>
                    <div className="mb-3">
                      <span className={sectionBadgeClassName}>Görünüm ve İlerleme</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Sayfalama Yöntemi</label>
                        <div className="space-y-3">
                          <label className={radioCardClass(form.pagination === "PAGINATION")}>
                            <input
                              type="radio"
                              name="pagination"
                              value="PAGINATION"
                              checked={form.pagination === "PAGINATION"}
                              onChange={handleChange}
                              className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-sm">Bölüm Başına Bir Sayfa</div>
                              <div className="text-xs text-gray-500 mt-1">Her bölüm ayrı sayfada gösterilir.</div>
                            </div>
                          </label>
                          <label className={radioCardClass(form.pagination === "SCROLL")}>
                            <input
                              type="radio"
                              name="pagination"
                              value="SCROLL"
                              checked={form.pagination === "SCROLL"}
                              onChange={handleChange}
                              className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-sm">Kaydırma</div>
                              <div className="text-xs text-gray-500 mt-1">Tüm bölümler tek sayfada gösterilir.</div>
                            </div>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">İlerleme Gösterimi</label>
                        <div className="space-y-3">
                          <label className={radioCardClass(form.processType === "PERCENTAGE")}>
                            <input
                              type="radio"
                              name="processType"
                              value="PERCENTAGE"
                              checked={form.processType === "PERCENTAGE"}
                              onChange={handleChange}
                              className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-sm">Yüzde</div>
                              <div className="text-xs text-gray-500 mt-1">İlerleme yüzde olarak gösterilir. (%75)</div>
                            </div>
                          </label>
                          <label className={radioCardClass(form.processType === "NUMBER")}>
                            <input
                              type="radio"
                              name="processType"
                              value="NUMBER"
                              checked={form.processType === "NUMBER"}
                              onChange={handleChange}
                              className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-sm">Sayı</div>
                              <div className="text-xs text-gray-500 mt-1">İlerleme sayı olarak gösterilir. (5/10)</div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={sectionClassName}>
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <span className={sectionBadgeClassName}>Eylemli Anket</span>
                        <h3 className="text-md font-semibold text-gray-900 mt-3">Onay ve Ret Aksiyonları</h3>
                        <p className="text-xs text-gray-500 mt-1">Onayla/Reddet kararlarında otomatik aksiyon uygulanır.</p>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer shrink-0 mt-1">
                        <input
                          type="checkbox"
                          checked={form.isActionEnabled}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              isActionEnabled: e.target.checked,
                              approveActionType: e.target.checked ? prev.approveActionType : "NONE",
                              rejectActionType: e.target.checked ? prev.rejectActionType : "NONE",
                              approveDepartmentId: e.target.checked ? prev.approveDepartmentId : "",
                              rejectDepartmentId: e.target.checked ? prev.rejectDepartmentId : "",
                            }))
                          }
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Etkinleştir</span>
                      </label>
                    </div>

                    {form.isActionEnabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4 pt-4 border-t border-slate-100">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Onay Aksiyonu</label>
                          <select
                            value={form.approveActionType}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                approveActionType: e.target.value,
                                approveDepartmentId:
                                  e.target.value === "ASSIGN_FIXED_DEPARTMENT" ? prev.approveDepartmentId : "",
                              }))
                            }
                            className={selectFieldClassName}
                          >
                            {SURVEY_ACTION_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {form.approveActionType === "ASSIGN_FIXED_DEPARTMENT" && (
                            <DepartmentSelector
                              value={form.approveDepartmentId || ""}
                              onChange={(departmentId) =>
                                setForm((prev) => ({ ...prev, approveDepartmentId: departmentId || "" }))
                              }
                              placeholder="Onay departmanını seçin"
                              departmentOptions={reviewDepartments}
                              openDirection="up"
                              className="w-full rounded-xl"
                            />
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Ret Aksiyonu</label>
                          <select
                            value={form.rejectActionType}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                rejectActionType: e.target.value,
                                rejectDepartmentId:
                                  e.target.value === "ASSIGN_FIXED_DEPARTMENT" ? prev.rejectDepartmentId : "",
                              }))
                            }
                            className={selectFieldClassName}
                          >
                            {SURVEY_ACTION_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {form.rejectActionType === "ASSIGN_FIXED_DEPARTMENT" && (
                            <DepartmentSelector
                              value={form.rejectDepartmentId || ""}
                              onChange={(departmentId) =>
                                setForm((prev) => ({ ...prev, rejectDepartmentId: departmentId || "" }))
                              }
                              placeholder="Ret departmanını seçin"
                              departmentOptions={reviewDepartments}
                              openDirection="up"
                              className="w-full rounded-xl"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">
                        İptal
                      </button>
                      <button
                        type="submit"
                        disabled={!form.title.trim() || isLoading}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 text-sm">
                        {isLoading ? "Güncelleniyor..." : "Anketi Güncelle"}
                      </button>
                    </div>
                  </div>
                </form>

                {id && (
                  <>
                    <div className={sectionClassName}>
                      <div className="mb-3">
                        <span className={sectionBadgeClassName}>Anket Bölümleri</span>
                      </div>
                      <SectionManager surveyId={id} canUpdateSurvey={hasEditPermission} />
                    </div>

                    <div className={sectionClassName}>
                      <div className="mb-3">
                        <span className={sectionBadgeClassName}>Katılımcılar</span>
                      </div>
                      <ParticipantManager surveyId={id} />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
